
import { useState, useEffect, useCallback } from "react";
import { Message } from "@/services/messages";

export function useOptimisticMessages(serverMessages: Message[]) {
  const [optimisticMessages, setOptimisticMessages] = useState<Message[]>([]);

  // Sync with server messages and filter out duplicates
  useEffect(() => {
    if (serverMessages.length === 0) return;
    
    // Remove optimistic messages when real ones arrive
    setOptimisticMessages(prev => {
      // Keep only temp messages that haven't been replaced by server messages
      const filteredOptimistic = prev.filter(optimisticMsg => {
        // Only keep temp messages
        if (!optimisticMsg.id.startsWith('temp-')) return false;
        
        // Check if a server message exists with same content and sender within 5 seconds
        const hasMatchingServerMessage = serverMessages.some(serverMsg => {
          const timeDiff = Math.abs(
            new Date(serverMsg.created_at).getTime() - 
            new Date(optimisticMsg.created_at).getTime()
          );
          return (
            serverMsg.content === optimisticMsg.content &&
            serverMsg.sender_id === optimisticMsg.sender_id &&
            timeDiff < 5000 // Within 5 seconds
          );
        });
        
        return !hasMatchingServerMessage;
      });
      
      return filteredOptimistic;
    });
  }, [serverMessages]);

  const addOptimisticMessage = useCallback((message: Message) => {
    setOptimisticMessages(prev => [...prev, message]);
  }, []);

  const updateOptimisticMessage = useCallback((id: string, updates: Partial<Message>) => {
    setOptimisticMessages(prev => 
      prev.map(msg => msg.id === id ? { ...msg, ...updates } : msg)
    );
  }, []);

  const removeOptimisticMessage = useCallback((id: string) => {
    setOptimisticMessages(prev => prev.filter(msg => msg.id !== id));
  }, []);

  // Combine server messages with optimistic messages, ensuring no duplicates
  const allMessages = [...serverMessages, ...optimisticMessages]
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  return {
    optimisticMessages: allMessages,
    addOptimisticMessage,
    updateOptimisticMessage,
    removeOptimisticMessage
  };
}
